import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MailOpen, Eye, PenLine, CheckCircle2, UserCheck, Clock, Loader2, RefreshCw, ArrowRight } from 'lucide-react';
import { InviteShareActions } from './InviteShareActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const font = { fontFamily: 'IBM Plex Sans, sans-serif' };

const STAGES = [
  { key: 'not_opened', label: 'Not opened', hint: 'Link sent, never clicked', icon: MailOpen, color: 'text-slate-600', bar: 'bg-slate-300' },
  { key: 'opened', label: 'Opened', hint: 'Clicked in, nothing entered yet', icon: Eye, color: 'text-blue-700', bar: 'bg-blue-400' },
  { key: 'mid_application', label: 'Mid-application', hint: 'Started the form, not submitted', icon: PenLine, color: 'text-amber-700', bar: 'bg-amber-400' },
  { key: 'submitted', label: 'Submitted', hint: 'Waiting for your approval', icon: CheckCircle2, color: 'text-emerald-700', bar: 'bg-emerald-500' },
  { key: 'active', label: 'Active', hint: 'Approved & driving', icon: UserCheck, color: 'text-[#002FA7]', bar: 'bg-[#002FA7]' },
];
const STEP_LABELS = { personal: 'Personal Info', cdl: 'CDL Info', type: 'Driver Type', documents: 'Documents' };

const idleTone = (d) => (d == null ? 'text-slate-400' : d >= 3 ? 'text-red-600' : d >= 1 ? 'text-amber-600' : 'text-slate-500');

const FunnelCard = ({ item, stage, companyName, senderName, onChanged }) => {
  const navigate = useNavigate();
  const stuck = ['not_opened', 'opened', 'mid_application'].includes(stage);
  return (
    <div data-testid={`funnel-card-${item.id}`} className="bg-white border border-slate-200 rounded-md p-3 shadow-sm" style={font}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{item.name || item.email}</p>
          <p className="text-[11px] text-slate-500 truncate">{item.email}{item.phone ? ` · ${item.phone}` : ''}</p>
        </div>
        <span className={`flex items-center gap-1 text-[10px] font-bold whitespace-nowrap ${idleTone(item.days_idle)}`} title={item.last_activity ? `Last activity ${new Date(item.last_activity).toLocaleString()}` : ''}>
          <Clock className="w-3 h-3" />{item.days_idle == null ? '—' : item.days_idle === 0 ? 'today' : `${item.days_idle}d idle`}
        </span>
      </div>
      {stage === 'mid_application' && (
        <p className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`funnel-step-${item.id}`}>
          Stopped at <strong>{STEP_LABELS[item.step] || 'Personal Info'}</strong>
        </p>
      )}
      {item.reminders > 0 && <p className="mt-1 text-[10px] text-slate-500">{item.reminders} auto-reminder{item.reminders === 1 ? '' : 's'} sent</p>}
      {stuck && (
        <div className="mt-2">
          <InviteShareActions invite={item} companyName={companyName} senderName={senderName} onResent={onChanged} compact />
        </div>
      )}
      {(stage === 'submitted' || stage === 'active') && item.driver_id && (
        <button type="button" onClick={() => navigate(`/drivers/${item.driver_id}`)} data-testid={`funnel-open-driver-${item.id}`}
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#002FA7] hover:underline">
          {stage === 'submitted' ? 'Review & approve' : 'Open driver'} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export const OnboardingFunnelBoard = ({ companyName, senderName, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpired, setShowExpired] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/driver-invitations/funnel`, { withCredentials: true });
      setData(res.data);
    } catch { /* keep previous */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const total = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0;
  if (!loading && total === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-6 mb-6" data-testid="onboarding-funnel-board">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Onboarding Funnel</h2>
          <p className="text-xs text-slate-500" style={font}>Where each invited driver is right now — nudge the stuck ones from here.</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.counts?.expired > 0 && (
            <button type="button" onClick={() => setShowExpired((v) => !v)} data-testid="funnel-toggle-expired" className="text-xs font-semibold text-slate-600 hover:text-slate-900" style={font}>
              {showExpired ? 'Hide' : 'Show'} expired ({data.counts.expired})
            </button>
          )}
          <button type="button" onClick={load} data-testid="funnel-refresh" className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50" title="Refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {data && (
        <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 mb-5" data-testid="funnel-progress-bar">
          {STAGES.map((s) => data.counts[s.key] > 0 && (
            <div key={s.key} className={`${s.bar} transition-all`} style={{ width: `${(data.counts[s.key] / Math.max(1, total - (data.counts.expired || 0))) * 100}%` }} title={`${s.label}: ${data.counts[s.key]}`} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {STAGES.map((s) => {
          const Icon = s.icon;
          const items = data?.stages?.[s.key] || [];
          return (
            <div key={s.key} data-testid={`funnel-column-${s.key}`} className="bg-slate-50 border border-slate-200 rounded-md p-2.5 min-h-[120px]">
              <div className="flex items-center justify-between mb-2 px-0.5">
                <div className="flex items-center gap-1.5">
                  <Icon className={`w-4 h-4 ${s.color}`} />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700" style={font}>{s.label}</span>
                </div>
                <span data-testid={`funnel-count-${s.key}`} className="text-xs font-bold text-slate-900 bg-white border border-slate-200 rounded-full px-2 py-0.5">{data?.counts?.[s.key] ?? 0}</span>
              </div>
              <p className="text-[10px] text-slate-400 mb-2 px-0.5" style={font}>{s.hint}</p>
              <div className="space-y-2">
                {items.map((it) => <FunnelCard key={it.id} item={it} stage={s.key} companyName={companyName} senderName={senderName} onChanged={load} />)}
                {!items.length && <p className="text-[11px] text-slate-400 italic px-0.5" style={font}>Nobody here</p>}
              </div>
            </div>
          );
        })}
      </div>

      {showExpired && data?.stages?.expired?.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-4" data-testid="funnel-expired-list">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2" style={font}>Expired invitations — resend to reactivate the link</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.stages.expired.map((it) => <FunnelCard key={it.id} item={it} stage="not_opened" companyName={companyName} senderName={senderName} onChanged={load} />)}
          </div>
        </div>
      )}
    </div>
  );
};
